'use client';

import { c as _c } from "react/compiler-runtime";
import { BottomDrawer, Portal } from '@common/components';
import { SvgDelete } from '@common/icon';
import { cn } from '@core/utils';
import { AnimatePresence, motion } from 'framer-motion';
import { useId } from 'react';
import { getMainWrapperElement } from '@/app/_components/MainWrapperPortal';
import { BREAKPOINTS } from '@/constants';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import type { ResponsiveDialogConfig } from '@/stores/ResponsiveDialogStore';
import { DonationModal } from '../molecule/DonationModal';
interface ResponsiveDialogProps {
  opened: boolean;
  onClose?: () => void;
  children?: React.ReactNode;
  /** drawer 헤더에 표시될 제목 */
  title?: ResponsiveDialogConfig['title'];
  ariaLabel?: ResponsiveDialogConfig['ariaLabel'];
  /** 모달의 header 부분 엘리먼트 */
  modalHeader?: ResponsiveDialogConfig['modalHeader'];
  duration?: number;
  /** bottom 형태인지 full 형태인지 결정 */
  drawerType?: 'bottom' | 'full';
  /** true일 경우 drawer content 영역의 padding 제거 (모바일만 적용) */
  drawerNoPadding?: boolean;
  /** true일 경우 full drawer 기본 헤더 숨기기 */
  hideDrawerHeader?: boolean;
  /** true일 경우 모바일에서도 모달로 표시 */
  forceModal?: boolean;
  /** true일 경우 앱바 스타일 헤더 적용 (왼쪽 X, 가운데 타이틀) */
  isAppBarHeader?: boolean;
  className?: {
    modal?: string;
    backdrop?: string;
    drawer?: string;
  };
}

/**
 * 데스크톱에서는 모달, 모바일에서는 드로워로 표시\
 * 직접 사용하기 보다는 openResponsiveDialog 유틸로 간결하게 사용
 */
export const ResponsiveDialog = t0 => {
  const $ = _c(41);
  const {
    opened,
    onClose,
    children,
    modalHeader,
    title,
    ariaLabel,
    duration: t1,
    drawerType: t2,
    drawerNoPadding,
    hideDrawerHeader,
    forceModal,
    isAppBarHeader,
    className
  } = t0;
  const duration = t1 === undefined ? 300 : t1;
  const drawerType = t2 === undefined ? "full" : t2;
  const isDesktopViewport = useMediaQuery(BREAKPOINTS.DESKTOP, true);
  const drawerTitleId = useId();
  if (isDesktopViewport || forceModal) {
    const t3 = className?.modal;
    let t4;
    if ($[0] !== children || $[1] !== modalHeader || $[2] !== onClose || $[3] !== opened || $[4] !== t3) {
      t4 = <DonationModal opened={opened} onClose={onClose} className={t3} header={modalHeader}>{children}</DonationModal>;
      $[0] = children;
      $[1] = modalHeader;
      $[2] = onClose;
      $[3] = opened;
      $[4] = t3;
      $[5] = t4;
    } else {
      t4 = $[5];
    }
    return t4;
  }
  if (drawerType === "bottom") {
    const t3 = title ? undefined : ariaLabel;
    const t4 = title ? drawerTitleId : undefined;
    const t5 = className?.backdrop;
    let t6;
    if ($[6] !== t5) {
      t6 = cn("fixed z-40", t5);
      $[6] = t5;
      $[7] = t6;
    } else {
      t6 = $[7];
    }
    const t7 = className?.drawer;
    let t8;
    if ($[8] !== t7) {
      t8 = cn("fixed z-50", t7);
      $[8] = t7;
      $[9] = t8;
    } else {
      t8 = $[9];
    }
    let t9;
    if ($[10] !== t6 || $[11] !== t8) {
      t9 = {
        backdrop: t6,
        drawer: t8
      };
      $[10] = t6;
      $[11] = t8;
      $[12] = t9;
    } else {
      t9 = $[12];
    }
    let t10;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <BottomDrawer.HandleWrapper><BottomDrawer.Handle /></BottomDrawer.HandleWrapper>;
      $[13] = t10;
    } else {
      t10 = $[13];
    }
    const t11 = !drawerNoPadding;
    let t12;
    if ($[14] !== drawerNoPadding || $[15] !== t11) {
      t12 = cn({
        "mx-20": t11,
        "mx-0": drawerNoPadding
      });
      $[14] = drawerNoPadding;
      $[15] = t11;
      $[16] = t12;
    } else {
      t12 = $[16];
    }
    let t13;
    if ($[17] !== drawerTitleId || $[18] !== title) {
      t13 = title ? <h2 id={drawerTitleId} className="mb-28 font-700 text-Gray-900 typo-body1">{title}</h2> : null;
      $[17] = drawerTitleId;
      $[18] = title;
      $[19] = t13;
    } else {
      t13 = $[19];
    }
    let t14;
    if ($[20] !== children || $[21] !== t12 || $[22] !== t13) {
      t14 = <BottomDrawer.Contents className={t12}>{t13}{children}</BottomDrawer.Contents>;
      $[20] = children;
      $[21] = t12;
      $[22] = t13;
      $[23] = t14;
    } else {
      t14 = $[23];
    }
    let t15;
    if ($[24] !== onClose || $[25] !== opened || $[26] !== t14 || $[27] !== t3 || $[28] !== t4 || $[29] !== t9) {
      t15 = <BottomDrawer opened={opened} onClose={onClose} aria-label={t3} aria-labelledby={t4} unmountOnExit={true} renderTo={getMainWrapperElement} className={t9}>{t10}{t14}</BottomDrawer>;
      $[24] = onClose;
      $[25] = opened;
      $[26] = t14;
      $[27] = t3;
      $[28] = t4;
      $[29] = t9;
      $[30] = t15;
    } else {
      t15 = $[30];
    }
    return t15;
  }
  let t3;
  if ($[31] !== children || $[32] !== className || $[33] !== drawerNoPadding || $[34] !== duration || $[35] !== hideDrawerHeader || $[36] !== isAppBarHeader || $[37] !== onClose || $[38] !== opened || $[39] !== title) {
    t3 = <FullDrawer opened={opened} onClose={onClose} title={title} duration={duration} isAppBarHeader={isAppBarHeader} drawerNoPadding={drawerNoPadding} hideDrawerHeader={hideDrawerHeader} className={className}>{children}</FullDrawer>;
    $[31] = children;
    $[32] = className;
    $[33] = drawerNoPadding;
    $[34] = duration;
    $[35] = hideDrawerHeader;
    $[36] = isAppBarHeader;
    $[37] = onClose;
    $[38] = opened;
    $[39] = title;
    $[40] = t3;
  } else {
    t3 = $[40];
  }
  return t3;
};
interface FullDrawerProps {
  opened: boolean;
  onClose?: () => void;
  children?: React.ReactNode;
  title?: string;
  duration: number;
  isAppBarHeader?: boolean;
  drawerNoPadding?: boolean;
  hideDrawerHeader?: boolean;
  className?: {
    backdrop?: string;
    drawer?: string;
  };
}
const FullDrawer = t0 => {
  const $ = _c(13);
  const {
    opened,
    onClose,
    children,
    title,
    duration,
    isAppBarHeader,
    drawerNoPadding,
    hideDrawerHeader,
    className
  } = t0;
  const durationInSeconds = duration / 1000;
  let t1;
  if ($[0] !== children || $[1] !== className?.backdrop || $[2] !== className?.drawer || $[3] !== drawerNoPadding || $[4] !== durationInSeconds || $[5] !== hideDrawerHeader || $[6] !== isAppBarHeader || $[7] !== onClose || $[8] !== opened || $[9] !== title) {
    t1 = opened && <><motion.div className={cn("fixed inset-0 z-40 bg-Gray-white", className?.backdrop)} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: durationInSeconds
      }} onClick={onClose} /><motion.div className={cn("fixed inset-0 z-50 flex flex-col bg-Gray-white", className?.drawer)} initial={{
        y: "20%",
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: "20%",
        opacity: 0
      }} transition={{
        type: "tween",
        duration: durationInSeconds,
        ease: "easeInOut"
      }}>{hideDrawerHeader ? null : <FullDrawerHeader title={title} isAppBarHeader={isAppBarHeader} onClose={onClose} />}<div className={cn("flex-1 overflow-auto", {
          "px-20": !drawerNoPadding,
          "px-0": drawerNoPadding
        })}>{children}</div></motion.div></>;
    $[0] = children;
    $[1] = className?.backdrop;
    $[2] = className?.drawer;
    $[3] = drawerNoPadding;
    $[4] = durationInSeconds;
    $[5] = hideDrawerHeader;
    $[6] = isAppBarHeader;
    $[7] = onClose;
    $[8] = opened;
    $[9] = title;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  let t2;
  if ($[11] !== t1) {
    t2 = <Portal renderTo={getMainWrapperElement}><AnimatePresence>{t1}</AnimatePresence></Portal>;
    $[11] = t1;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  return t2;
};
interface FullDrawerHeaderProps {
  title?: string;
  isAppBarHeader?: boolean;
  onClose?: () => void;
}
const FullDrawerHeader = t0 => {
  const $ = _c(9);
  const {
    title,
    isAppBarHeader,
    onClose
  } = t0;
  const t1 = !isAppBarHeader;
  let t2;
  if ($[0] !== t1) {
    t2 = cn("flex min-h-56 items-center px-20", {
      "justify-between": t1
    });
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== isAppBarHeader || $[3] !== onClose || $[4] !== title) {
    t3 = isAppBarHeader ? <><button type="button" onClick={onClose} className="flex size-24 items-center justify-center" aria-label={"\uB2EB\uAE30"}><SvgDelete className="size-24 text-Gray-900" /></button><h2 className="flex-1 text-center font-600 text-Gray-900 typo-body1">{title || ""}</h2><div className="size-24" /></> : <><h2 className="font-700 text-Gray-900 typo-h6">{title || ""}</h2><button type="button" onClick={onClose} className="flex size-32 items-center justify-center" aria-label={"\uB2EB\uAE30"}><SvgDelete /></button></>;
    $[2] = isAppBarHeader;
    $[3] = onClose;
    $[4] = title;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <header className={t2}>{t3}</header>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
};